import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { AlertCircle, AlertTriangle, Battery, RotateCcw, X } from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import {
  getNaerasPoolAsync,
  revertNaerasPoolAsync,
  updateNaerasPoolAsync,
} from '@/data/store/actions';
import { getAllUsers, getCurrentSessionUser } from '@/data/store/selectors';
import { TNaerasPoolState } from '@/data/types';
import { parseNaerasError } from '@/pages/Calculate/utils/naerasBid';
import { POOL_SOURCE_META, athensToday } from '../utils/pool';

interface PoolEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a save or a revert, so the caller can refetch what depends on the pool. */
  onChanged: () => void;
}

const POOL_DATE_INVALID = 'NAERAS_POOL_DATE_INVALID';

const formatMwh = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${value.toFixed(2)} MWh`;

/**
 * Who set a manual value, as far as the portal can tell: the signed-in user,
 * or anyone in the user list if it happens to be loaded. Otherwise unnamed.
 */
function useEditorName(profileId: string | null | undefined): string | null {
  const self = useSelector(getCurrentSessionUser);
  const users = useSelector(getAllUsers);

  if (!profileId) return null;
  if (self?.profiles?.some((profile) => profile.id === profileId)) return 'εσάς';

  const user = users.find((item) => item.profile_id === profileId);

  return user ? `${user.first_name} ${user.last_name}`.trim() : null;
}

/**
 * Sets the Hybrid Pool (Απόθεμα Υβριδικής Ενέργειας) at 00:00 of a day by hand.
 *
 * Meant for the morning after an ACROSS outage, when the pool is stale and
 * every bid would be capped by an old number. A manual value wins over Job A
 * for that day, later days chain from it, and the backend alerts the
 * operations channel — so saving asks for a confirmation first.
 */
export const PoolEditDialog: React.FC<PoolEditDialogProps> = ({ isOpen, onClose, onChanged }) => {
  const dispatch = useAppDispatch();
  const today = athensToday();

  const [date, setDate] = useState(today);
  const [current, setCurrent] = useState<TNaerasPoolState | null>(null);
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editorName = useEditorName(current?.manual?.set_by_id);

  // Start over every time the dialog opens
  useEffect(() => {
    if (!isOpen) return;

    setDate(today);
    setIsConfirming(false);
    setError(null);
    setDateError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The value already in force for the chosen day, to edit from
  useEffect(() => {
    if (!isOpen || !date) return;

    let cancelled = false;
    setIsLoading(true);
    setIsConfirming(false);

    dispatch(getNaerasPoolAsync({ params: { date } }))
      .unwrap()
      .then((pool) => {
        if (cancelled) return;
        setCurrent(pool);
        setValue(pool.value.toFixed(2));
      })
      .catch(() => {
        if (!cancelled) setCurrent(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isOpen, date]);

  if (!isOpen) return null;

  const parsed = Number(value.replace(',', '.'));
  const isValueValid = value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
  const isDateValid = Boolean(date) && date <= today;
  const isUnchanged = current !== null && Math.abs(parsed - current.value) < 0.005;
  const isBusy = isLoading || isSaving;

  const handleFailure = (reason: unknown, fallback: string) => {
    const { code, message } = parseNaerasError(reason);

    if (code === POOL_DATE_INVALID) {
      setDateError(message ?? 'Μη έγκυρη ημερομηνία.');
    } else {
      setError(message ?? fallback);
    }

    setIsConfirming(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const pool = await dispatch(
        updateNaerasPoolAsync({ payload: { date, value: parsed }, silent: true })
      ).unwrap();

      setCurrent(pool);
      setIsConfirming(false);
      onChanged();
      onClose();
    } catch (reason) {
      handleFailure(reason, 'Η τιμή δεν αποθηκεύτηκε.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevert = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const pool = await dispatch(revertNaerasPoolAsync({ date, silent: true })).unwrap();

      setCurrent(pool);
      setValue(pool.value.toFixed(2));
      onChanged();
    } catch (reason) {
      handleFailure(reason, 'Η επαναφορά απέτυχε.');
    } finally {
      setIsSaving(false);
    }
  };

  const sourceMeta = current ? POOL_SOURCE_META[current.source] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-3xl border border-gray-100 bg-white p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-xl bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
            <Battery className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Απόθεμα Υβριδικής Ενέργειας</h3>
            <p className="text-xs text-gray-500">Χειροκίνητος ορισμός της τιμής στις 00:00 μιας ημέρας</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-bold text-gray-700">Ημέρα</label>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(event) => {
                setDate(event.target.value);
                setDateError(null);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
            />
            {(dateError || (date && !isDateValid)) && (
              <p className="mt-1 text-xs text-rose-600">
                {dateError ?? 'Επιτρέπεται μόνο σήμερα ή προηγούμενη ημέρα.'}
              </p>
            )}
          </div>

          {/* What the day holds now */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-xs">
            {isLoading ? (
              <div className="h-10 animate-pulse rounded-xl bg-gray-100" />
            ) : current && sourceMeta ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">Τρέχουσα τιμή</span>
                  <span className="font-mono font-bold text-gray-900">{formatMwh(current.value)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">Προέλευση</span>
                  <span className={`font-bold ${sourceMeta.healthy ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {sourceMeta.label}
                    {current.source === 'stale' && ` — ${current.stale_days} ημέρες πίσω`}
                  </span>
                </div>
                {current.manual && (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-500">Ορίστηκε</span>
                      <span className="text-right text-gray-800">
                        {editorName ? `από ${editorName}, ` : ''}
                        {format(new Date(current.manual.set_at), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-500">Αντικατέστησε</span>
                      <span className="font-mono text-gray-800">{formatMwh(current.manual.previous_value)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-500">Υπολογισμός Εργασίας Α</span>
                      <span className="font-mono text-gray-800">
                        {current.manual.reconciled_value === null
                          ? 'δεν έχει γίνει'
                          : formatMwh(current.manual.reconciled_value)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Δεν βρέθηκε τιμή για την ημέρα αυτή.</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold text-gray-700">Νέα τιμή (MWh)</label>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                setIsConfirming(false);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 font-mono text-sm focus:border-emerald-400 focus:outline-none"
            />
            {value.trim() !== '' && !isValueValid && (
              <p className="mt-1 text-xs text-rose-600">Η τιμή πρέπει να είναι αριθμός, 0 ή μεγαλύτερος.</p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {isConfirming && current && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <span>
                Το απόθεμα της {format(parseISO(date), 'dd/MM/yyyy')} θα αλλάξει από{' '}
                <strong>{formatMwh(current.value)}</strong> σε <strong>{formatMwh(parsed)}</strong>. Η τιμή
                υπερισχύει της Εργασίας Α για την ημέρα αυτή, μεταφέρεται στις επόμενες ημέρες και
                στέλνεται ειδοποίηση στην ομάδα λειτουργίας.
              </span>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {current?.source === 'manual' ? (
            <button
              onClick={handleRevert}
              disabled={isBusy}
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Επαναφορά στην υπολογισμένη τιμή
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            {isConfirming && (
              <button
                onClick={() => setIsConfirming(false)}
                disabled={isBusy}
                className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                Πίσω
              </button>
            )}
            <button
              onClick={isConfirming ? handleSave : () => setIsConfirming(true)}
              disabled={isBusy || !isValueValid || !isDateValid || isUnchanged}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
            >
              {isSaving ? 'Αποθήκευση...' : isConfirming ? 'Επιβεβαίωση' : 'Αποθήκευση'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoolEditDialog;
